import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, GraduationCap, Palette, QrCode, User } from 'lucide-react';

export const interactiveExhibit: Playbook = {
  meta: {
    id: 'interactive-exhibit',
    slug: 'interactive-exhibit',
    categoryId: 'physical-world',
    name: 'The Interactive Exhibition Guide',
    tagline: 'Make every exhibit come alive.',
    description: 'Place QR codes next to museum exhibits, art pieces, or educational displays. Visitors scan for audio tours, detailed information, interactive Q&A, and personalized learning experiences.',
    strategy: 'Place QR codes next to museum exhibits, art pieces, or educational displays. Visitors scan for audio tours, detailed information, interactive Q&A, and personalized learning experiences.',
    benefits: [
      'Interactive learning experiences',
      'Personalized educational content',
      'Increased visitor engagement',
      'Multilingual support capability'
    ],
    targetAudience: ['Museums', 'Art galleries', 'Educational institutions', 'Visitor centers'],
    estimatedSetupTime: '35 minutes',
    difficulty: 'intermediate',
    tags: ['museums', 'education', 'interactive-learning', 'exhibitions'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Interactive Exhibition Guide Playbook - QR Code Museum Experience',
    metaDescription: 'Transform museum visits with interactive QR code Genie guides. Personalized learning, audio tours, and educational Q&A for every exhibit.',
    keywords: ['interactive exhibition', 'museum guide', 'educational Genie', 'qr code tours', 'visitor experience']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "🎨 Welcome! You're viewing 'Starry Night' by Van Gogh. I can tell you about the painting's history, the artist's technique, or answer any questions. Would you like the 2-minute story or explore something specific?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Welcome! You're viewing 'Starry Night'. Want the story behind this masterpiece?" },
    { speaker: 'user', message: "Yes, tell me about it" },
    { speaker: 'agent', message: "Van Gogh painted this from his asylum room in 1889, from memory rather than observation." },
    { speaker: 'user', message: "Why the swirling sky?" },
    { speaker: 'agent', message: "The swirls represent turbulence and emotion - Van Gogh was exploring his inner turmoil through the night sky. Some theorists even link it to mathematical turbulence patterns!" }
  ],
  templateConfig: {
    firstMessage: "🎨 Welcome! You're viewing [EXHIBIT]. How would you like to explore it?",
    systemPrompt: "You are an interactive museum guide Genie accessed via QR code next to exhibits. Provide engaging information, answer questions, and create personalized learning experiences. Be educational, engaging, and adapt to different interest levels.",
    tools: ['exhibit_database', 'audio_narration', 'related_exhibits', 'quiz_mode', 'visitor_feedback']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Visitor approaches exhibit',
      description: 'Museum guest stops at artwork, artifact, or display and scans QR code'
    },
    {
      type: 'genie',
      icon: QrCode,
      genieIcon: 'genie-lamp',
      label: 'Identifies specific exhibit',
      description: 'Genie instantly knows which piece visitor is viewing and loads relevant content'
    },
    {
      type: 'action',
      icon: Palette,
      genieIcon: 'share',
      label: 'Shares exhibit story',
      description: 'Provides historical context, artistic techniques, and cultural significance'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'settings',
      label: 'Enables interactive exploration',
      description: 'Answers specific questions and adapts content to visitor interest level'
    },
    {
      type: 'action',
      icon: GraduationCap,
      genieIcon: 'share',
      label: 'Offers deeper learning',
      description: 'Suggests related exhibits, provides quiz mode, or shares expert insights'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Enriched visitor experience',
      description: 'Deeper understanding and engagement with educational content'
    }
  ],
  metrics: {
    conversionRate: '68%',
    responseTime: '<1 second',
    satisfactionScore: '4.9/5',
    costSavings: '$25k/month',
    timeToValue: 'Immediate'
  },
  testimonials: [
    {
      companyName: 'Metropolitan Art Museum',
      authorName: 'Dr. Sarah Chen',
      authorTitle: 'Visitor Experience Director',
      quote: 'Interactive guides increased average visit duration by 45% and visitor satisfaction by 35%. Guests love the personalized learning experience.',
      metrics: '45% longer visit duration'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Educational Content Structure',
      description: 'Framework for organizing exhibit information for different learning styles',
      url: '/templates/educational-content'
    },
    {
      type: 'guide',
      title: 'Museum QR Code Placement',
      description: 'Best practices for positioning codes near exhibits without disrupting experience',
      url: '/docs/museum-qr-placement'
    }
  ]
};